import type { ButtonProps } from './Button';
import type { ButtonAppearance, ButtonAppearanceProps, ButtonSize } from '@natter-co/design-system';

/**
 * Maps the legacy MUI `Button` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at 244 call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS components cannot express this button" and sends the caller back
 * to MUI. That is a deliberate design: a silent best-effort mapping would ship wrong-looking or
 * behaviourally different buttons, and there is no way to notice at review time. Every `null` below
 * is a design question that has to be answered before that call site can be refreshed.
 */
type DsProps = ButtonAppearanceProps & { size: ButtonSize; loading?: boolean };

/** Every Type has Default; the outlined legacy variant maps to Muted where Figma draws it. */
const APPEARANCE: Partial<Record<NonNullable<ButtonProps['color']>, ButtonAppearance>> = {
  primary: 'primary',
  secondary: 'secondary',
  error: 'destructive',
};

/**
 * Mapped by HEIGHT, not by name.
 *
 * MUI buttons are 24 / 32 / 40 (`MuiButton.ts` sizeSmall/Medium/Large); DS buttons are 24 / 32 / 40
 * / 44. Matching the names instead of the heights would move every size onto a different step, so
 * turning the flag on could silently grow buttons and reflow whatever sat beside them. Only exact
 * height matches are mapped:
 *
 *   small  (24) -> 2xs     (24)
 *   medium (32) -> xs      (32)
 *   large  (40) -> regular (40)
 *
 * That is the same rule `Buttons/IconButton/toDsIconProps` applies, apart from the documented 20px
 * icon-button exception in that mapper.
 */
const SIZE: Partial<Record<NonNullable<ButtonProps['size']>, ButtonSize>> = {
  small: '2xs',
  medium: 'xs',
  large: 'regular',
};

/**
 * Cases the DS components cannot express, all of which stay on MUI:
 *
 *   - `loadingIndicator` — a caller-supplied MUI spinner. The DS button draws its own, so honouring
 *     a custom one would mean rendering an Emotion `CircularProgress` inside a Tailwind button.
 *   - `sx` — an Emotion prop with no effect on a Tailwind component. Accepting it would silently
 *     drop the styles rather than fail, so ~40 call sites would quietly lose their spacing.
 *   - `variant="text-no-padding" | "grayed"` — theme-local variants
 *     (`common/theme/components/MuiButton.ts`) with no DS counterpart. `text-no-padding` in
 *     particular is a zero-height inline text button, which the recipe's fixed heights rule out.
 *   - `color="info"` — see above.
 *   - a disabled link — the platform has no such thing, and `ButtonLink` accordingly has no
 *     `disabled` prop. Rare enough to leave on MUI rather than design around.
 */
export const toDsProps = ({
  color = 'primary',
  disabled,
  externalHref,
  href,
  isLoading,
  loadingIndicator,
  size = 'medium',
  sx,
  variant = 'contained',
}: ButtonProps): DsProps | null => {
  if (sx || loadingIndicator) {
    return null;
  }

  // A link cannot be disabled or busy: `ButtonLink` has no such state, and the platform has no
  // inert anchor.
  if ((href || externalHref) && (disabled || isLoading)) {
    return null;
  }

  const dsSize = SIZE[size];

  if (!dsSize) {
    return null;
  }

  const base = { size: dsSize, loading: isLoading };

  // Early returns rather than one built object: `ButtonAppearanceProps` is a discriminated union, so
  // a flat `{ appearance, fill }` assembled from variables satisfies neither member.
  if (variant === 'text') {
    // Tertiary / Default is the borderless text action in the Figma matrix.
    return { appearance: 'tertiary', fill: 'default', ...base };
  }

  /**
   * The old neutral outline becomes Secondary / Default, the current Figma Cancel cell.
   *
   * `color` is ignored on purpose. The MUI variant hardcodes its own colours and overrides whatever
   * `color` was passed (`MuiButton.ts:144-164`), so honouring `color` here would make the DS render
   * differ from the MUI one it replaces — `Dialog.tsx` passes `color="secondary"` that today has no
   * effect whatsoever.
   */
  if (variant === 'outlined-grey') {
    return { appearance: 'secondary', fill: 'default', ...base };
  }

  /**
   * The pale-blue tint becomes Primary / Muted, the same brand tint. `color` is ignored, as for
   * `outlined-grey`: the MUI variant hardcodes the primary tint whatever `color` says.
   */
  if (variant === 'light') {
    return { appearance: 'primary', fill: 'muted', ...base };
  }

  if (variant !== 'contained' && variant !== 'outlined') {
    return null;
  }

  const appearance = APPEARANCE[color];

  if (!appearance) return null;
  if (variant === 'outlined') {
    if (appearance === 'primary' || appearance === 'destructive' || appearance === 'success') {
      return { appearance, fill: 'muted', ...base };
    }
    return { appearance: 'secondary', fill: 'default', ...base };
  }
  return { appearance, fill: 'default', ...base };
};
